import { useCallback, useMemo, useRef } from "react";
import { useMemberPlanState } from "./useMemberPlanState";
import { useMemberStatus } from "./useMemberStatus";
import { useActiveWorkout, validateActiveWorkout } from "./useActiveWorkout";
import { useDietPlans, useUserProfile, useTrainingRecords, useCustomPlan } from './useAppState';
import { scheduleFields, validClock, frequencyForDays } from '@/lib/personalSchedule';
import { isTrainingRecordComplete } from '@/lib/trainingRecordCompletion';
import userStorage, { getCurrentUserId } from '@/lib/user-storage';
import type { DietPlanTemplate } from '@/data/diet-plan-templates';
import { emptyTrainingSchedule } from '@/pages/MemberTrainingPlanPage/schedule/emptySchedule';
import { commitTrainingSchedule, scheduleStamp } from '@/pages/MemberTrainingPlanPage/schedule/scheduleDates';
import { validateScheduleDays } from '@/pages/MemberTrainingPlanPage/schedule/scheduleContent';
import { useScheduleDate } from '@/pages/MemberTrainingPlanPage/schedule/useScheduleDate';
import { localTrainingDate, scheduledMemberDay, type MemberTrainingPlan,
  type MemberPlanDay, type DailyTrainingOverride } from "@/lib/memberTrainingDay";

export const useTrainingDate = useScheduleDate;
export function useTrainingSchedule() {
  const { member } = useMemberStatus();
  const { activeWorkout } = useActiveWorkout();
  const [stored, setStored] = useMemberPlanState();
  const { profile, updateProfile } = useUserProfile();
  const { savedCurrentPlan, updatePlan } = useDietPlans();
  const { records } = useTrainingRecords();
  const { plan: customPlan } = useCustomPlan();
  const owner = getCurrentUserId();
  const newId = useRef(`custom-schedule-${crypto.randomUUID()}`);
  const base = useMemo(() => emptyTrainingSchedule(profile, (customPlan.dietPlanTemplate || 'generic') as DietPlanTemplate, newId.current), [profile, customPlan.dietPlanTemplate]);
  const today = useTrainingDate();
  const plan = member.isMember ? stored : null;
  const requireEditable = useCallback((creating = false) => {
    if (getCurrentUserId() !== owner) throw new Error('账号已切换，请重新打开排期');
    if (!member.isMember || (!stored && !creating)) throw new Error("会员专属，请先生成定制计划");
    if (activeWorkout.active || validateActiveWorkout(userStorage.getItem('active_workout')).valid) throw new Error("请先完成或结束当前训练");
  }, [member.isMember, stored, activeWorkout.active, owner]);
  const saveSchedule = (days: MemberPlanDay[], effectiveDate = today, expected = scheduleStamp(stored)) => {
    requireEditable(true);
    validateScheduleDays(days);
    if (effectiveDate === today && records.some(r => r.date === today && isTrainingRecordComplete(r))) throw new Error('今天已完成训练，请选择明天生效');
    if (days.some(d => d.type !== 'rest' && d.startTime && !validClock(d.startTime))) throw new Error('请填写有效的开始时间');
    let updated: MemberTrainingPlan | null = null;
    setStored(previous => {
      if (scheduleStamp(previous) !== expected) throw new Error("计划已更新，请重新打开排期面板");
      updated = commitTrainingSchedule(previous || base, days, effectiveDate, localTrainingDate(), () => crypto.randomUUID());
      return updated;
    });
    if (!updated) return;
    const saved = updated as MemberTrainingPlan;
    const version = saved.scheduleHistory?.find(v => v.effectiveFrom === effectiveDate) || saved;
    const nutritionHours = version.weeklyAvailableHours ?? version.weeklyHours;
    if (effectiveDate <= today) updateProfile({ ...scheduleFields(version), strengthDays: version.weekDays.filter(d => d.type === 'strength').map(d => d.dayOfWeek), trainingFrequency: frequencyForDays(version.weekDays.filter(d => d.type !== 'rest').length), weeklyTrainingHours: nutritionHours });
    const replacedDate = stored?.pendingSchedule?.effectiveDate;
    if (savedCurrentPlan) updatePlan(savedCurrentPlan.id, { weeklyHours: nutritionHours,
      weeklyHoursHistory: [...(savedCurrentPlan.weeklyHoursHistory || [{ effectiveFrom: '0000-01-01', weeklyHours: savedCurrentPlan.weeklyHours }])
        .filter(v => v.effectiveFrom !== effectiveDate && !(replacedDate && replacedDate > today && replacedDate !== effectiveDate && v.effectiveFrom === replacedDate)),
        { effectiveFrom: effectiveDate, weeklyHours: nutritionHours }] });
  };
  const replaceToday = (day: MemberPlanDay, kind: DailyTrainingOverride["kind"]) => {
    requireEditable();
    setStored(previous => {
      if (!previous) return previous;
      if (previous.id !== stored?.id) throw new Error("计划已更新，请重新打开换课面板");
      const originalDay = previous.dailyOverrides?.[today]?.originalDay || scheduledMemberDay(previous);
      if (!originalDay) return previous;
      const id = crypto.randomUUID();
      const replacement: DailyTrainingOverride = { id, date: today, kind, originalDay,
        day: { ...day, dayOfWeek: originalDay.dayOfWeek, virtualId: `member-swap-${id}` } };
      return { ...previous, dailyOverrides: { ...previous.dailyOverrides, [today]: replacement } };
    });
  };
  return { plan, editorPlan: member.isMember ? stored || base : null,
    scheduleDisabled: !member.isMember || activeWorkout.active, storedStamp: scheduleStamp(stored), today,
    override: plan?.dailyOverrides?.[today], saveSchedule, replaceToday, disabled: !plan || activeWorkout.active };
}
